import { useCallback, useEffect, useMemo, useState } from 'react'
import Meta from '../../components/Meta'
import {
  GetShippingByCodeDocument,
  GetShippingByCodeQuery,
  GetShippingByCodeQueryVariables,
  useGetLaboratoryShippingByLaboratoryIdQuery,
  useInsertLaboratoryShippingMutation,
  useUpdateLaboratoryShippingMutation,
} from '../../generated/urql.administrator'
import { Button, FloatingLabel, Form } from 'react-bootstrap'
import { useForm, Controller } from 'react-hook-form'
import { InpostGeowidgetReact } from 'inpost-geowidget-react'
import { InpostGeowidget } from '../../const'
import toastsService from '../../lib/toastsService'
import { useClient } from 'urql'

type LaboratoryShippingPointProps = {
  id: number
}

const pageTitle = 'Punkt paczkowy'

type MapApi = {
  changeLanguage: (lang: string) => void
  changePointsType: () => void
  changePosition: () => void
  changeZoomLevel: () => void
  clearSearch: () => void
  hideSearchResult: () => void
  search: () => void
  selectPoint: (name: string) => void
  showPoint: (name: string) => void
  showPointDetails: () => void
}
type MapPoint = {
  name: string
  address: { line1: string; line2: string }
}

type ShippingForm = {
  email: string
  phone: string
  point: MapPoint
}

const LaboratoryShippingPoint = ({ id }: LaboratoryShippingPointProps) => {
  const client = useClient()
  const [isSaving, setIsSaving] = useState(false)
  const [mapApi, setMapApi] = useState<MapApi>()
  const [{ data, fetching: isLoading }] =
    useGetLaboratoryShippingByLaboratoryIdQuery({
      pause: !id,
      variables: { laboratoryId: id },
    })
  const [, updateLabShipping] = useUpdateLaboratoryShippingMutation()
  const [, insertLabShipping] = useInsertLaboratoryShippingMutation()
  const {
    handleSubmit,
    register,
    reset,
    control,
    formState: { errors },
  } = useForm<ShippingForm>()

  const shippingData = !!data?.laboratory_shipping?.length
    ? data.laboratory_shipping[0]
    : undefined

  const apiReady = (api: MapApi) => setMapApi(api)

  const onSave = useCallback(
    async ({ email, phone, point }: ShippingForm) => {
      setIsSaving(true)
      let error
      if (shippingData?.id) {
        error = await updateLabShipping({
          id: shippingData.id,
          email,
          phone,
          pointName: point.name,
        }).then((res) => res.error)
      } else if (id) {
        const { data: inpostShipping, error: shippingError } = await client
          .query<GetShippingByCodeQuery, GetShippingByCodeQueryVariables>(
            GetShippingByCodeDocument,
            { code: 'inpost_locker_standard' }
          )
          .toPromise()

        error = shippingError

        const shippingId = !!inpostShipping?.shipping?.length
          ? inpostShipping?.shipping[0].id
          : undefined

        if (shippingId) {
          error = await insertLabShipping({
            email,
            phone,
            pointName: point.name,
            laboratoryId: id,
            shippingId,
          }).then((res) => res.error)
        } else {
          error = { message: 'Wysyłka inpost nie została skonfigurowana' }
        }
      }
      if (error) {
        toastsService.add({
          title: 'Błąd!',
          message: error.message || 'Coś poszło nie tak. Spróbuj ponownie.',
          variant: 'danger',
        })
      } else {
        toastsService.add({
          title: 'Sukces!',
          message: 'Pomyślnie zapisano zmiany.',
          variant: 'success',
        })
      }
      setIsSaving(false)
    },
    [shippingData, updateLabShipping]
  )

  useEffect(() => {
    if (shippingData && mapApi) {
      const { email, phone, pointName } = shippingData
      reset((values) => ({ ...values, email, phone }))
      mapApi.selectPoint(pointName)
      mapApi.showPoint(pointName)
    }
  }, [shippingData, mapApi])

  return (
    <div>
      <Meta title={pageTitle} />
      <div className="py-3 d-flex gap-3 align-items-center">
        <h4 className="m-0">Dane do przesyłek paczkomatem</h4>
        {isLoading && (
          <span
            className="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
            style={{ width: 24, height: 24 }}
          />
        )}
      </div>

      <Form onSubmit={handleSubmit(onSave)}>
        <FloatingLabel label="Email" className="mb-3">
          <Form.Control
            isInvalid={!!errors.email}
            type="email"
            placeholder="Email"
            {...register('email', {
              required: 'Proszę podać email',
              pattern: {
                value:
                  /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
                message: 'Nieprawidłowy format',
              },
            })}
          />
          {errors.email && (
            <p style={{ fontSize: 12, color: 'red' }}>{errors.email.message}</p>
          )}
        </FloatingLabel>
        <FloatingLabel label="Numer telefonu" className="mb-3">
          <Form.Control
            isInvalid={!!errors.phone}
            type="tel"
            placeholder="Numer telefonu"
            {...register('phone', {
              required: 'Proszę podać numer telefonu',
              pattern: {
                value: /^((00|\+)\d{1,3})? ?(\d[ -]?){9}$/,
                message: 'Podany numer telefonu jest niepoprawny',
              },
            })}
          />
          {errors.phone && (
            <p style={{ fontSize: 12, color: 'red' }}>{errors.phone.message}</p>
          )}
        </FloatingLabel>
        <Controller
          control={control}
          rules={{
            required: 'Proszę wybrać punkt odbioru',
          }}
          name="point"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <div className="d-flex flex-column mb-3">
              <div
                className={
                  !value
                    ? 'd-none'
                    : 'd-flex gap-3 justify-content-between border rounded'
                }
                style={{ paddingInline: 12, paddingBlock: 10 }}
              >
                <div className="d-flex flex-column">
                  <span>
                    Wybrano punkt: <strong>{value?.name}</strong>
                  </span>
                  <span>
                    {value?.address.line1}, {value?.address.line2}
                  </span>
                </div>
                <Button onClick={() => onChange(undefined)}>Zmień</Button>
              </div>
              <div className={!!value ? 'd-none' : 'd-flex flex-column gap-3'}>
                <span>Wybierz punkt odbioru</span>
                <div className="w-full" style={{ height: 500 }}>
                  <InpostGeowidgetReact
                    token={InpostGeowidget.token}
                    sandbox={InpostGeowidget.sandbox}
                    apiReady={apiReady}
                    pointSelect={({ name, address }: MapPoint) =>
                      onChange({ name, address })
                    }
                  />
                </div>
              </div>
              {errors.point && (
                <p style={{ fontSize: 12, color: 'red' }}>
                  {errors.point.message}
                </p>
              )}
            </div>
          )}
        />
        <div className="d-flex justify-content-end">
          <Button
            variant="success"
            type="submit"
            disabled={isSaving}
            className="d-flex gap-2 justify-content-center align-items-center"
            style={{ width: 100 }}
          >
            {isSaving && (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            )}
            Zapisz
          </Button>
        </div>
      </Form>
    </div>
  )
}

export default LaboratoryShippingPoint
